<template>
  <div
    class="flex flex-col rounded-md"
    :class="{
      'bg-transparent': backgroundColor === 'transparent',
      'bg-lexmea-gray-100': backgroundColor === 'grey',
    }"
  >
    <div
      class="rounded-t-md py-1 md:px-1"
      :class="{
        'rounded-b-md': collapsed,
        'max-md:flex max-md:flex-col max-md:items-center': columnCollapsed,
      }"
    >
      <div class="flex items-center justify-between" :class="titleStyles">
        <button
          v-if="titleAsCollapseToggle"
          class="w-full truncate text-start"
          @click="collapsed = !collapsed"
        >
          <slot name="title" :collapsed="collapsed" />
        </button>

        <div v-else class="w-full truncate">
          <slot name="title" :collapsed="collapsed" />
        </div>

        <div class="flex shrink-0 gap-0">
          <slot name="action" />
          <button
            v-if="!columnCollapsed"
            v-tooltip.bottom="
              !collapsed ? $t('ui.collapse_box') : $t('ui.expand_box')
            "
            class="flex items-center justify-center rounded-full transition hover:bg-gray-100"
            :class="[
              {
                'max-sm:hidden': hideDropdownOnMobile,
                'h-6 w-6': !columnCollapsed,
                'h-5 w-5': columnCollapsed,
              },
              toggleExpandButtonStyles,
            ]"
            :title="collapsed ? t('ui.expand') : t('ui.collapse')"
            :aria-label="collapsed ? t('ui.expand') : t('ui.collapse')"
            @click="collapsed = !collapsed"
          >
            <i-heroicons-chevron-down-20-solid
              class="transform transition"
              :class="{
                'rotate-90': collapsed,
              }"
            />
          </button>
        </div>
      </div>
      <button
        v-if="titleAsCollapseToggle"
        class="w-full truncate text-start"
        @click="collapsed = !collapsed"
      >
        <slot name="subtitle" :collapsed="collapsed" />
      </button>

      <div v-else v-show="!collapsed" class="truncate">
        <slot name="subtitle" :collapsed="collapsed" />
      </div>
      <div
        v-show="!collapsed"
        class="w-full border-b-2 border-lexmea-gray-500"
        :class="dividerStyles"
      ></div>
    </div>
    <div
      v-if="!collapsed"
      class="flex min-h-0 flex-col rounded-b-md text-sm"
      :class="{ 'overflow-y-auto': overflow }"
    >
      <slot />
    </div>
  </div>
</template>

<script lang="ts" setup>
const { t } = useI18n();
const props = withDefaults(
  defineProps<{
    columnCollapsed: boolean;
    overflow?: boolean;
    collapse?: boolean;
    backgroundColor?: "grey" | "transparent";
    titleAsCollapseToggle?: boolean;
    dividerStyles?: string;
    titleStyles?: string;
    toggleExpandButtonStyles?: string;
    hideDropdownOnMobile?: boolean;
  }>(),
  {
    backgroundColor: "grey",
    overflow: true,
    collapse: false,
    hideDropdownOnMobile: false,
    titleAsCollapseToggle: false,
    dividerStyles: undefined,
    titleStyles: undefined,
    toggleExpandButtonStyles: undefined,
  }
);

const emits = defineEmits<{
  expanded: [];
}>();

const collapsed = ref(props.collapse);

whenever(
  () => !collapsed.value,
  () => emits("expanded")
);

watch(
  () => props.collapse,
  (newValue) => (collapsed.value = newValue)
);
</script>
